import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { EmailInput } from "@/components/ui/EmailInput";
import { TextInput } from "@/components/ui/TextInput";
import { PreviewSection, PhoneEditor, PhoneType, AddressEditor, AddressData } from "../shared";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { UserType } from "@/types/db/users";
import { useTheme } from "@/hooks/useTheme";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { PositionsInput } from "@/components/inputs/PositionsInput";
import { AbilitiesInput } from "@/components/inputs/AbilitiesInput";
import { RestrictionsInput } from "@/components/inputs/RestrictionsInput";
import { supabase } from "@/lib/supabase-client";
import useToast from "@/hooks/useToast";
import usePermissions from "@/hooks/usePermissions";
import useAuth from "@/hooks/useAuth";
import { UserAccountSection } from "./UserAccountSection";
/** @deprecated Use `UserType` directly — kept as an alias during the UserData→UserType alignment. */
export type UserData = UserType;

export interface UserOverviewTabProps {
  user: UserType | null;
  loading?: boolean;
}

export function UserOverviewTab({ user, loading = false }: UserOverviewTabProps) {
  // Get edit context
  const { editing, formData, updateFormData } = useEntityEdit<UserType>();
  const { colors } = useTheme();
  const { user_private: privatePermissions } = usePermissions();
  const { session } = useAuth();

  // Permission check for user_private (address section)
  const canReadUserPrivate = privatePermissions.read;
  const canUpdateUserPrivate = privatePermissions.update;

  const [emailError, setEmailError] = useState<string | undefined>();
  const [emailChecking, setEmailChecking] = useState(false);
  const [cellPhoneError, setCellPhoneError] = useState<string | undefined>();
  const [cellPhoneChecking, setCellPhoneChecking] = useState(false);
  const toast = useToast();

  // Check if email already exists (debounced) - only in edit mode
  useEffect(() => {
    if (!editing) {
      setEmailError(undefined);
      setEmailChecking(false);
      return;
    }

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = formData?.email_address?.trim().toLowerCase();
    const originalEmail = user?.email_address?.trim().toLowerCase();

    // Clear error and skip check if email is empty, invalid format, or unchanged
    if (!email || !EMAIL_REGEX.test(email) || email === originalEmail) {
      setEmailError(undefined);
      setEmailChecking(false);
      return;
    }

    setEmailChecking(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("id")
          .ilike("email_address", email)
          .neq("id", user?.id || "") // Exclude current user
          .limit(1);

        if (error) {
          console.error("Error checking email:", error);
          setEmailError(undefined);
        } else if (data && data.length > 0) {
          setEmailError("Email already exists");
        } else {
          setEmailError(undefined);
        }
      } catch (err) {
        console.error("Error checking email:", err);
        setEmailError(undefined);
      } finally {
        setEmailChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData?.email_address, user?.email_address, user?.id, editing]);

  // Check if cell phone already exists (debounced) - only in edit mode
  useEffect(() => {
    if (!editing) {
      setCellPhoneError(undefined);
      setCellPhoneChecking(false);
      return;
    }

    // Get cell phone from form data
    const cellPhone = formData?.meta_data?.phones?.find((p: any) => p.type === "cell")?.number;
    const originalCellPhone = user?.meta_data?.phones?.find((p: any) => p.type === "cell")?.number;

    // Normalize phone numbers for comparison (remove non-digits)
    const normalizedCell = cellPhone?.replace(/\D/g, "");
    const normalizedOriginal = originalCellPhone?.replace(/\D/g, "");

    // Clear error and skip check if phone is empty, too short, or unchanged
    if (!normalizedCell || normalizedCell.length < 10 || normalizedCell === normalizedOriginal) {
      setCellPhoneError(undefined);
      setCellPhoneChecking(false);
      return;
    }

    setCellPhoneChecking(true);
    const timeoutId = setTimeout(async () => {
      try {
        // Search for users with matching cell phone in meta_data->phones array
        const { data, error } = await supabase
          .from("users")
          .select("id, meta_data")
          .neq("id", user?.id || "");

        if (error) {
          console.error("Error checking cell phone:", error);
          setCellPhoneError(undefined);
        } else if (data) {
          // Check if any user has this cell phone
          const exists = data.some((u) => {
            const phones = u.meta_data?.phones || [];
            return phones.some((p: any) => p.type === "cell" && p.number?.replace(/\D/g, "") === normalizedCell);
          });

          if (exists) {
            setCellPhoneError("Cell phone already exists");
          } else {
            setCellPhoneError(undefined);
          }
        }
      } catch (err) {
        console.error("Error checking cell phone:", err);
        setCellPhoneError(undefined);
      } finally {
        setCellPhoneChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData?.meta_data?.phones, user?.meta_data?.phones, user?.id, editing]);

  // Helper function to add a new phone
  const handleAddPhone = () => {
    const currentPhones = formData?.meta_data?.phones || [];
    const usedTypes = new Set(currentPhones.map((p) => p.type));

    // Find first available type
    const availableTypes: PhoneType[] = ["cell", "home", "work"];
    const nextType = availableTypes.find((type) => !usedTypes.has(type));

    if (!nextType) {
      toast.showToast({
        title: "Maximum phones reached",
        message: "You can only add one phone per type (cell, home, work)",
        toastType: "warning",
      });
      return;
    }

    const newPhone = {
      type: nextType,
      number: "",
    };

    updateFormData({
      ...formData,
      meta_data: {
        ...formData?.meta_data,
        phones: [...currentPhones, newPhone],
      },
    });
  };

  // Unified data source - edit mode uses formData, view mode uses user
  const displayData = editing ? formData : user;
  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 16, padding: 16 }}>
        <PreviewSection title="Contact Information">
          <EmailInput
            label="Email"
            value={editing ? formData?.email_address || "" : displayData?.email_address || ""}
            onChangeText={(text) => updateFormData({ ...formData, email_address: text })}
            error={emailError}
            checking={emailChecking}
            editable={editing}
            loading={loading}
          />
          <TextInput
            label="Preferred Name"
            value={displayData?.preferred_name || ""}
            onChangeText={(text) => updateFormData({ ...formData, preferred_name: text })}
            editable={editing}
            loading={loading}
          />
        </PreviewSection>

        <PreviewSection
          title="Phones"
          button={
            editing
              ? {
                  icon: "plus",
                  onPress: handleAddPhone,
                  disabled: (displayData?.meta_data?.phones?.length || 0) >= 3,
                }
              : undefined
          }
          editing={editing}
        >
          <PhoneEditor
            phones={displayData?.meta_data?.phones || []}
            onChange={(phones) =>
              updateFormData({
                ...formData,
                meta_data: {
                  ...formData?.meta_data,
                  phones,
                },
              })
            }
            editing={editing}
            loading={loading}
            cellPhoneError={cellPhoneError}
            cellPhoneChecking={cellPhoneChecking}
          />
        </PreviewSection>

        <PreviewSection title="Employment Information">
          <TextInput
            label="Job Title"
            value={displayData?.job_title || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                job_title: text,
              })
            }
            editable={editing}
            loading={loading}
          />
          <TextInput
            label="Job Description"
            value={displayData?.job_description || ""}
            onChangeText={(text) =>
              updateFormData({
                ...formData,
                job_description: text,
              })
            }
            editable={editing}
            loading={loading}
            multiline
            numberOfLines={3}
          />
        </PreviewSection>

        <View style={[styles.toggleRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={{ flex: 1 }}>
            <ThemedText style={styles.toggleLabel}>Supervisor Eligible</ThemedText>
            <ThemedText style={[styles.toggleHelper, { color: colors.textSecondary }]}>
              Can be assigned as shift supervisor
            </ThemedText>
          </View>
          {editing ? (
            <ToggleSwitch
              value={displayData?.meta_data?.supervisor_eligible ?? false}
              onValueChange={(value) => {
                updateFormData({
                  ...formData,
                  meta_data: {
                    ...formData.meta_data,
                    supervisor_eligible: value,
                  },
                });
              }}
            />
          ) : (
            <ThemedText style={{ fontWeight: "500" }}>
              {displayData?.meta_data?.supervisor_eligible ? "Yes" : "No"}
            </ThemedText>
          )}
        </View>

        {canReadUserPrivate && (
          <PreviewSection title="Address">
            <AddressEditor
              address={displayData?.user_private?.[0]?.addresses as AddressData | undefined}
              onChange={(addresses) => {
                if (!editing || !canUpdateUserPrivate) return;
                // user_private is an array from the DB, get the first element
                const currentPrivate = Array.isArray(formData?.user_private)
                  ? formData?.user_private?.[0]
                  : formData?.user_private;
                updateFormData({
                  ...formData,
                  user_private: [{ ...(currentPrivate || {}), addresses }],
                } as any);
              }}
              editing={editing && canUpdateUserPrivate}
              loading={loading}
            />
          </PreviewSection>
        )}

        {user?.id && session?.access_token && (
          <UserAccountSection userId={user.id} accessToken={session.access_token} editing={editing} />
        )}

        <MarketsInput
          value={(displayData?.meta_data as any)?.markets || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(ids) =>
            updateFormData({
              ...formData,
              meta_data: { ...formData.meta_data, markets: ids },
            } as any)
          }
        />

        <PositionsInput
          value={displayData?.user_positions || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          onChange={(positions) => updateFormData({ ...formData, user_positions: positions } as any)}
        />

        <AbilitiesInput
          value={displayData?.user_abilities || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          showExpiry
          onChange={(abilities) => updateFormData({ ...formData, user_abilities: abilities } as any)}
        />

        <RestrictionsInput
          value={displayData?.user_restrictions || []}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          showExpiry
          onChange={(restrictions) => updateFormData({ ...formData, user_restrictions: restrictions } as any)}
        />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 2,
  },
  toggleHelper: {
    fontSize: 12,
  },
});
